/**
 * Secure storage utilities for sensitive data
 * Provides additional security measures for browser storage
 */

interface SecureStorageOptions {
  ttl?: number; // Time to live in milliseconds
  encrypt?: boolean; // Whether to apply basic encoding
}

interface StoredData {
  value: any;
  timestamp: number;
  ttl?: number;
}

/**
 * Secure wrapper for sessionStorage
 * Adds expiration and basic obfuscation for sensitive data
 */
export class SecureSessionStorage {
  private static readonly PREFIX = '__secure_';
  
  /**
   * Store data securely with automatic expiration
   */
  static setItem(key: string, value: any, options: SecureStorageOptions = {}): void {
    try {
      const data: StoredData = {
        value: options.encrypt ? this.encode(value) : value,
        timestamp: Date.now(),
        ttl: options.ttl
      };
      
      sessionStorage.setItem(
        this.PREFIX + key, 
        JSON.stringify(data)
      );
      
      // Set automatic cleanup if TTL is specified
      if (options.ttl) {
        setTimeout(() => {
          this.removeItem(key);
        }, options.ttl);
      }
    } catch (error) {
      console.warn('Failed to store secure data:', error);
    }
  }
  
  /**
   * Retrieve data with automatic expiration check
   */
  static getItem(key: string, decrypt = false): any | null {
    try {
      const stored = sessionStorage.getItem(this.PREFIX + key);
      if (!stored) return null;
      
      const data: StoredData = JSON.parse(stored);
      
      // Check if data has expired
      if (data.ttl && Date.now() - data.timestamp > data.ttl) {
        this.removeItem(key);
        return null;
      }
      
      return decrypt ? this.decode(data.value) : data.value;
    } catch (error) {
      console.warn('Failed to retrieve secure data:', error);
      return null;
    }
  }
  
  /**
   * Remove item and clear any timers
   */
  static removeItem(key: string): void {
    try {
      sessionStorage.removeItem(this.PREFIX + key);
    } catch (error) {
      console.warn('Failed to remove secure data:', error);
    }
  }
  
  /**
   * Clear all secure storage items
   */
  static clear(): void {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(this.PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear secure storage:', error);
    }
  }
  
  /**
   * Check if an item exists and is not expired
   */
  static hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }
  
  /**
   * Basic encoding for sensitive data (not cryptographically secure)
   * Provides minimal obfuscation to prevent casual inspection
   */
  private static encode(data: string): string {
    try {
      return btoa(encodeURIComponent(data));
    } catch {
      return data;
    }
  }
  
  /**
   * Decode previously encoded data
   */
  private static decode(encoded: string): string {
    try {
      return decodeURIComponent(atob(encoded));
    } catch {
      return encoded;
    }
  }
}

/**
 * Specifically for Stripe session data
 * Automatically expires after 1 hour for security
 */
export class StripeSessionStorage {
  private static readonly SESSION_TTL = 60 * 60 * 1000; // 1 hour
  private static readonly TIER_TTL = 60 * 60 * 1000; // 1 hour
  
  static setStripeSession(sessionId: string): void {
    SecureSessionStorage.setItem('stripe_session_verified', sessionId, {
      ttl: this.SESSION_TTL,
      encrypt: true
    });
  }
  
  static getStripeSession(): string | null {
    return SecureSessionStorage.getItem('stripe_session_verified', true);
  }
  
  static setSubscriptionTier(tier: string): void {
    SecureSessionStorage.setItem('subscription_tier', tier, {
      ttl: this.TIER_TTL,
      encrypt: true
    });
  }
  
  static getSubscriptionTier(): string | null {
    return SecureSessionStorage.getItem('subscription_tier', true);
  }
  
  static clearStripeData(): void {
    SecureSessionStorage.removeItem('stripe_session_verified');
    SecureSessionStorage.removeItem('subscription_tier');
  }
  
  static hasValidStripeSession(): boolean {
    return SecureSessionStorage.hasItem('stripe_session_verified') && 
           SecureSessionStorage.hasItem('subscription_tier');
  }
}

/**
 * Clean up expired items on page load
 */
export function cleanupExpiredStorage(): void {
  // This will be called on app initialization
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(SecureSessionStorage['PREFIX'])) {
        // Accessing the item will automatically clean it up if expired
        const data = sessionStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed.ttl && Date.now() - parsed.timestamp > parsed.ttl) {
              sessionStorage.removeItem(key);
            }
          } catch {
            // Invalid data, remove it
            sessionStorage.removeItem(key);
          }
        }
      }
    });
  } catch (error) {
    console.warn('Failed to cleanup expired storage:', error);
  }
}